import { BACKEND_STATE, EDITOR_VIEW_NAME, THEME } from "../core/constants";
 
import { isResponsiveView } from "../features/responsive";
import { MenuArea } from "../components/MenuArea";
import { AppIFrame } from "../components/AppIFrame";
import { CodeControlsMenu } from "../components/CodeControlsMenu";
import { EditorNavMenu } from "../components/EditorNavMenu";
import { ProjectCoordinates } from "../components/ProjectCoordinates";
import { ProjectNameLabel } from "../components/ProjectNameLabel";
import LanguageBadge from "../components/labels/LanguageBadge";
import { ProgressIndicator } from "../components/labels/ProgressIndicator";
import { MainMenu } from "../components/MainMenu";
import { ResultCode } from "../components/ResultCode";
import { faRotateLeft, faRotateRight, faListCheck, faTv, faRobot, faChevronRight, faCirclePlay, faExpand, faXmark, faFolderOpen } from '@fortawesome/free-solid-svg-icons';

import { getErrorCode, getStartCode, initUserCodeSession } from "../features/coderunner";
import { QuestionModal } from "../components/modals/QuestionModal";
import { HelpTaskArea } from "../components/HelpTaskArea";
import { getDomainCfg, store } from "../core/state";
import { KEY_EVENT, requestNextStep, StepPopper } from "../components/StepPopper";
import { AssetsLib } from "../components/assetslib/AssetsLib";
import { isShareButtonVisible, ShareModal, showShareModal } from "../components/modals/ShareModal";
import { LoginRegisterModal } from "../components/modals/LoginRegisterModal";

import { useEffect, useRef, useState, useCallback } from "react";
import { useBrowserFullscreen } from "../hooks/useBrowserFullscreen";
import { CodeEntryHelper } from "../components/codeentryhelper/CodeEntryHelper";
import { ProjectEditor } from "../components/ProjectEditor";
import { getCustomMenuItem } from "../features/custommenuitem";
import { PartyManager } from "../components/collaboration/PartyManager";
import { Onboarding } from "../components/Onboarding";
import { TaskArea } from "../components/taskarea/TaskArea";
import { PopperHelper } from "../components/PopperHelper";
import { EditProjectDataModal } from "../components/EditProjectDataModal";
import { FullscreenOverlay } from "../components/FullscreenOverlay";
import { OutputFullscreen } from "../components/OutputFullscreen";
import { saveUserData } from "../core/code/saver";
import { getViewData, setViewData } from "../core/views";
import { ProjectBackendStatus, getStatusIndicator } from "../components/ProjectBackendStatus";
import { backendActive, getBackendStatusText } from "../features/backend";
import { SolutionArea, solutionAreaShown } from "../components/SolutionArea";
import { RequestSupportButton } from "../components/buttons/RequestSupportButton";
import { InfoToast } from "../components/InfoToast";
import { CodeEntryAreaSwitch } from "../components/codeentryhelper/CodeEntryAreaSwitch";
import { CodeEntryHelperArea } from "../components/codeentryhelper/CodeEntryHelperArea";
import { TaskAreaSwitch } from "../components/taskarea/TaskAreaSwitch";
import { TaskAreaCard } from "../components/taskarea/TaskAreaCard";
import { SupportChatSwitch } from "../components/chat/SupportChatSwitch";
import SupportChat from "../components/chat/SupportChat";
import { Tab, Tabs, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AuthenticatedSupportChat from "../components/chat/AuthenticatedSupportChat";
import { AgentChat } from "../components/chat/AgentChat";
import { getCurrentTheme } from "../components/ThemeContext";
import { translate } from "../core/i18n";
import { RoundButton } from "../components/buttons/base/RoundButton";
import ModernHeaderButton from "../components/buttons/base/ModernHeaderButton";
import { FilePanel } from "../components/filesystem/FilePanel";
import { 
  shouldUseVFS, 
  getFileDefinitions, 
  FileDefinition,
  processUploadedFile,
  downloadProjectAsZip,
  createNewFile,
  getLanguageFromFilename
} from "../features/filesystem";

// Constants outside component to avoid issues with hooks
const LEFT_PANEL_WIDTH = 350; // left panel (tasks/files) - default width
const LEFT_PANEL_WIDTH_NARROW = 220; // left panel width for screens < 1400px
const DIVIDER_WIDTH = 8;
const MIN_PANEL_WIDTH = 300;

export function EditorView() {
  // All hooks must be declared at the top, before any other code
  const [editorState, setEditorState] = store.useState(EDITOR_VIEW_NAME);
  const [backendState] = store.useState(BACKEND_STATE);
  const [splitWidth, setSplitWidth] = useState("500");
  const [showCodeEntryHelperArea, setShowCodeEntryHelperArea] = useState(false);
  const [showTaskArea, setShowTaskArea] = useState(true);
  const [showResponsiveSupport, setShowResponsiveSupport] = useState(false);
  const [currentTheme, setCurrentTheme] = store.useState(THEME);
  const [activeTab, setActiveTab] = useState<string>('app');
  const splitRef : any = useRef();
  const [chatAreaIntegration, setChatAreaIntegration] = useState("floating");
  const [isChatVisible, setIsChatVisible] = useState(false);
  
  // Custom divider state between code (center) and output (right)
  const containerRef = useRef<HTMLDivElement>(null);
  const dividerRef = useRef<HTMLDivElement>(null);
  const pointerIdRef = useRef<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [startCodeWidth, setStartCodeWidth] = useState(600);
  const [codePanelWidth, setCodePanelWidth] = useState(600);
  const [savedCodePanelWidth, setSavedCodePanelWidth] = useState<number | null>(null);
  const [tasksCollapsed, setTasksCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const [leftPanelTab, setLeftPanelTab] = useState<'tasks' | 'files'>('tasks');
  const [isNarrowScreen, setIsNarrowScreen] = useState(window.innerWidth < 1400);
  
  // Horizontal divider state for responsive view (between editor and output)
  const horizontalDividerRef = useRef<HTMLDivElement>(null);
  const horizontalPointerIdRef = useRef<number | null>(null);
  const [horizontalDragging, setHorizontalDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [startEditorHeight, setStartEditorHeight] = useState(300);
  const [editorHeight, setEditorHeight] = useState(300);
  const outputContentRef = useRef<HTMLDivElement>(null);
  const responsiveOutputRef = useRef<HTMLDivElement>(null);
  const { isInFullscreen, requestFullscreen, exitFullscreen } = useBrowserFullscreen();

  const switchToFloatingChat = useCallback(() => {
    setChatAreaIntegration("floating");
    setIsChatVisible(true);
  }, []);

  const toggleRightSidebar = useCallback(() => {
    const containerWidth = containerRef.current?.clientWidth || 0;
    const hasTaskContentLocal = editorState.project?.task || editorState.project?.subtasks || editorState.project?.steps;
    const taskAreaEnabledLocal = editorState.cfg?.editorFeatures?.showTaskButton && hasTaskContentLocal;

    // V2 projects can show the left *files* panel even when task accordion is disabled.
    // In that case, the left panel still consumes width and must be included in effectiveLeft.
    const isV2ProjectLocal = shouldUseVFS(editorState?.project);
    const v2TabsLocal = editorState?.data?.projectContent?.tabs || [];
    const filesEnabledLocal = isV2ProjectLocal && v2TabsLocal.length > 0;

    const currentLeftPanelWidth = isNarrowScreen ? LEFT_PANEL_WIDTH_NARROW : LEFT_PANEL_WIDTH;
    const effectiveLeft = (taskAreaEnabledLocal || filesEnabledLocal) ? currentLeftPanelWidth : 0;

    if (!rightCollapsed) {
      try { setSavedCodePanelWidth(codePanelWidth); } catch {}
      const maxCode = containerWidth - effectiveLeft - DIVIDER_WIDTH - 50;
      // Clamp to the actually available width so the right panel (and its toggle) can't get clipped.
      // If maxCode < MIN_PANEL_WIDTH, using MIN_PANEL_WIDTH would push the output panel out of view.
      setCodePanelWidth(Math.max(0, maxCode));
      setRightCollapsed(true);
    } else {
      const restore =
        savedCodePanelWidth ??
        Math.max(MIN_PANEL_WIDTH, containerWidth - effectiveLeft - DIVIDER_WIDTH - MIN_PANEL_WIDTH);
      setCodePanelWidth(restore);
      setRightCollapsed(false);
    }
  }, [
    rightCollapsed,
    codePanelWidth,
    savedCodePanelWidth,
    isNarrowScreen,
    editorState,
  ]);


  // Beim Verlassen/Reload: V2-Änderungen persistieren (VFS + LS + Cloud), best-effort
  useEffect(() => {
    const onBeforeUnload = () => {
      if (shouldUseVFS(editorState?.project)) saveUserData();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [editorState?.project]);

  // Initialize activeTab and chatAreaIntegration based on editorState
  useEffect(() => {
    if (editorState?.cfg?.editorFeatures?.showChatByDefault) {
      setActiveTab('support');
    }
    if (editorState?.cfg?.editorFeatures?.defaultChatIntegration) {
      setChatAreaIntegration(editorState.cfg.editorFeatures.defaultChatIntegration);
    }
  }, [editorState?.cfg?.editorFeatures?.showChatByDefault, editorState?.cfg?.editorFeatures?.defaultChatIntegration]);

  // Set default left panel tab based on project type
  useEffect(() => {
    const isV2 = shouldUseVFS(editorState?.project);
    const hasTaskContentLocal = editorState?.project?.task || editorState?.project?.subtasks || editorState?.project?.steps;
    const taskAreaEnabledLocal = editorState?.cfg?.editorFeatures?.showTaskButton && hasTaskContentLocal;
    
    // If V2 project and no tasks, show files tab by default
    if (isV2 && !taskAreaEnabledLocal) {
      setLeftPanelTab('files');
    } else if (taskAreaEnabledLocal) {
      setLeftPanelTab('tasks');
    }
  }, [editorState?.project, editorState?.cfg?.editorFeatures?.showTaskButton]);

  // Initialize currentTheme if empty
  useEffect(() => {
    if (Object.keys(currentTheme).length === 0) {
      const theme = getCurrentTheme();
      setCurrentTheme(theme);
    }
  }, [currentTheme]);

  // Always expand the right output panel on view entry / reload.
  useEffect(() => {
    setRightCollapsed(false);
  }, []);

  // Note: we intentionally do NOT reset `rightCollapsed` on project changes here.
  // Otherwise the user click "Einklappen" can be undone immediately by re-renders/state updates.

  // Monitor window width for responsive left panel width and adjust code panel width
  useEffect(() => {
    const updateLayout = () => {
      const nowNarrow = window.innerWidth < 1400;
      setIsNarrowScreen(nowNarrow);
      
      // Code-Panel-Breite anpassen (nur wenn nicht gerade gezogen wird)
      if (!dragging && containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        if (containerWidth === 0) return; // Container noch nicht gerendert
        
        const hasTaskContentLocal = editorState.project?.task || editorState.project?.subtasks || editorState.project?.steps;
        const taskAreaEnabledLocal = editorState.cfg?.editorFeatures?.showTaskButton && hasTaskContentLocal;
        
        if (nowNarrow) {
          // Bei schmalen Bildschirmen: 50% der verfügbaren Breite
          const currentLeftPanelWidth = LEFT_PANEL_WIDTH_NARROW;
          const effectiveLeft = taskAreaEnabledLocal ? currentLeftPanelWidth : 0;
          const availableWidth = containerWidth - effectiveLeft - DIVIDER_WIDTH;
          const newCodeWidth = Math.floor(availableWidth / 2);
          if (newCodeWidth >= MIN_PANEL_WIDTH) {
            setCodePanelWidth(newCodeWidth);
          }
        }
      }
    };
    
    // Initial setzen mit kleinem Delay, damit Container gerendert ist
    const timeoutId = setTimeout(updateLayout, 100);
    
    window.addEventListener('resize', updateLayout);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateLayout);
    };
  }, [dragging, editorState]);

  // Global pointer listeners for the resizable divider
  useEffect(() => {
    if (!dragging) return;
    
    const hasTaskContentLocal = editorState.project?.task || editorState.project?.subtasks || editorState.project?.steps;
    const taskAreaEnabledLocal = editorState.cfg?.editorFeatures?.showTaskButton && hasTaskContentLocal;
    
    const onPointerMove = (e: PointerEvent) => {
      const containerWidth = containerRef.current?.clientWidth || 0;
      const delta = e.clientX - dragStartX;
      let newWidth = startCodeWidth + delta;
      const currentLeftPanelWidth = isNarrowScreen ? LEFT_PANEL_WIDTH_NARROW : LEFT_PANEL_WIDTH;
      const effectiveLeft = taskAreaEnabledLocal ? currentLeftPanelWidth : 0;
      const effectiveRightMin = rightCollapsed ? 50 : MIN_PANEL_WIDTH;
      const maxCode = containerWidth - effectiveLeft - DIVIDER_WIDTH - effectiveRightMin;
      if (newWidth < MIN_PANEL_WIDTH) newWidth = MIN_PANEL_WIDTH;
      if (maxCode > 0 && newWidth > maxCode) newWidth = maxCode;
      setCodePanelWidth(newWidth);
    };
    
    const onPointerUp = () => {
      if (dividerRef.current && pointerIdRef.current !== null) {
        dividerRef.current.releasePointerCapture(pointerIdRef.current);
        pointerIdRef.current = null;
      }
      setDragging(false);
    };
    
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    
    return () => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
    };
  }, [dragging, dragStartX, startCodeWidth, rightCollapsed, editorState, isNarrowScreen]);

  // Global pointer listeners for the horizontal resizable divider (responsive view)
  useEffect(() => {
    if (!horizontalDragging) return;
    
    const onPointerMove = (e: PointerEvent) => {
      e.preventDefault();
      const delta = e.clientY - dragStartY;
      let newHeight = startEditorHeight + delta;
      const minHeight = 150;
      const headerHeight = 48; // responsive header height
      const bottomMenuHeight = 60; // bottom menu height
      const maxHeight = window.innerHeight - headerHeight - bottomMenuHeight - 20; // Leave some padding
      if (newHeight < minHeight) newHeight = minHeight;
      if (newHeight > maxHeight) newHeight = maxHeight;
      setEditorHeight(newHeight);
    };
    
    const onPointerUp = (e: PointerEvent) => {
      e.preventDefault();
      if (horizontalDividerRef.current && horizontalPointerIdRef.current !== null) {
        try {
          horizontalDividerRef.current.releasePointerCapture(horizontalPointerIdRef.current);
        } catch (err) {
          // Ignore if already released
        }
        horizontalPointerIdRef.current = null;
      }
      setHorizontalDragging(false);
    };
    
    document.addEventListener('pointermove', onPointerMove, { passive: false });
    document.addEventListener('pointerup', onPointerUp, { passive: false });
    document.addEventListener('pointercancel', onPointerUp, { passive: false });
    
    return () => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerUp);
    };
  }, [horizontalDragging, dragStartY, startEditorHeight]);

  // Set activeTab based on editorState.data.activeTab if it exists
  useEffect(() => {
    console.log("EditorView: activeTab", editorState?.data?.activeTab);
    if (editorState?.data?.activeTab) {

      setActiveTab(editorState.data.activeTab);
    }
  }, [editorState?.data?.activeTab]);

  // Initialize backend session once when editor becomes visible
  // This happens after the editor is rendered, so user can interact immediately
  useEffect(() => {
    const initializeBackend = async () => {
      // Only initialize if:
      // 1. Editor state is valid (project loaded)
      // 2. Backend is active for this project
      // 3. No backend session exists yet
      // 4. Not already initializing
      if (!editorState?.project || !editorState?.data?.projectContent) {
        return; // Editor not fully loaded yet
      }

      if (!backendActive(editorState)) {
        return; // Not a backend project
      }

      const backendSession = backendState?.userSession;
      const isInitializing = backendState?.initializing;
      
      if (backendSession || isInitializing) {
        console.log("CO-BACKEND: EditorView -> backend already initialized or initializing, skipping");
        return;
      }

      console.log("CO-BACKEND: EditorView -> initializing backend session (editor is now visible)");
      const initCode = getStartCode(editorState);
      if (initCode) {
        await initUserCodeSession(editorState, initCode);
      }
    };

    initializeBackend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorState?.project?.id, editorState?.data?.projectContent]); // Only run when project changes

  const splitterSizeChanged = () => {
    setSplitWidth(splitRef.state.primaryPane.toString());
  }

  const handleKeyUp = (e) => {
    requestNextStep(KEY_EVENT, e.code)
  }

  const switchCodeEntryArea = () => {    
    setShowCodeEntryHelperArea(!showCodeEntryHelperArea);
    setShowTaskArea(false);
    setShowResponsiveSupport(false);
  }

  const switchTaskArea = () => {    
    setShowCodeEntryHelperArea(false);
    setShowTaskArea(!showTaskArea);
    setShowResponsiveSupport(false);
  }

  const showSupportChatArea = () => {
    setShowCodeEntryHelperArea(false);
    setShowTaskArea(false);
    setShowResponsiveSupport(!showResponsiveSupport);
  }

  const showAppArea = () => {
    setShowCodeEntryHelperArea(false);
    setShowTaskArea(false);
    setShowResponsiveSupport(false);
  }

  const resetOutput = () => {
    try {
      setEditorState({ ...editorState, data: { ...editorState.data, time: Date.now(), executionRequested: false, executionCode: '', rightTab: editorState.data?.rightTab } });
    } catch {}
  }

  const responsiveView = isResponsiveView();

  const showAssetsLib = editorState?.data?.assetsVisible;
  const showEditData = editorState?.data?.editDataVisible;
  const showIframeView = !showAssetsLib && !showEditData;
  const assetsLib = <AssetsLib />;
  const executeCode = (() => {
    if (editorState?.data?.executionCode) {
      console.log("CO-BACKEND: EditorView - using existing executionCode");
      return editorState.data.executionCode;
    } else {
      console.log("CO-BACKEND: EditorView - calling getStartCode (no executionCode)");
      return getStartCode(editorState);
    }
  })();
  const errorCode = getErrorCode(editorState);  
  const timeStamp = editorState?.data?.time;

  const editorLayout = editorState?.cfg?.editorFeatures?.layout;
  const lpdeLayout = editorLayout === "lpde";

  const appFrame = (
    <>
      <AppIFrame doc={errorCode || executeCode}
                 timeStamp={timeStamp}
                 classNames={lpdeLayout ? "" : "ratio ratio-16x9"}
                 fullScreen={false}
                 landscape={false}
                 muteSound={false}
                 hasAppBorder={false}
                 highlight={editorState?.data?.executionRequested} />
      <ResultCode />
    </>
  );

  let navBarItems = null;
  if (!responsiveView) {
    const navbarItemsDesktop = editorState.cfg?.menus?.navbarright?.map((item, index) => {
      if (item.type === "component") {
        if (item.name === "codecontrolsmenu") return null; // moved to editor header actions
        else if (item.name === "editornavmenu") return null; // moved to editor header actions
        else if (item.name === "mainmenu") return null; // burger will be on the right in header actions
        else if (item.name === "projectcoordinates") return <ProjectCoordinates key={index} />;
        else if (item.name === "projectbackendstatus") return <ProjectBackendStatus key={index} />;
        else if (item.name === "projectnamelabel") return <ProjectNameLabel project={editorState?.project} key={index} />;
      } else if (item.type === "link" || item.type === "button") {
        return getCustomMenuItem(item,index);
      }
      return null;
    })
    navBarItems = navbarItemsDesktop;
  } else {
    const navbarItemsResponsive = editorState.cfg?.menus?.navbarright?.map((item, index) => {
      if (item.type === "component") {        
        if (item.name === "codecontrolsmenu") return null;
        else if (item.name === "editornavmenu") return null;
        else if (item.name === "mainmenu") return null;
        else if (item.name === "projectcoordinates") return <ProjectCoordinates key={index} />;
        else if (item.name === "projectbackendstatus") return <ProjectBackendStatus key={index} />;
      } else if (item.type === "link" || item.type === "button") {
        return getCustomMenuItem(item,index);
      }
      return null;
    })
    navBarItems = navbarItemsResponsive;
  }
  
  const menu = null;

/*
  const hasHelpContent = ( editorState.project?.hint || editorState?.project?.subTasks?.length > 0);
  const showHelpTaskArea = editorState.cfg?.editorFeatures?.helpActive && hasHelpContent;
  const helpTaskArea = showHelpTaskArea && <HelpTaskArea />;  
  const solutionArea = solutionAreaShown(editorState) && <SolutionArea width={splitWidth} />;*/

  const hasTaskContent = editorState.project?.task || editorState.project?.subtasks || editorState.project?.steps;
  const taskAreaEnabled = editorState.cfg?.editorFeatures?.showTaskButton && hasTaskContent;
  
  // Version 2.0 Filesystem Support
  const isV2Project = shouldUseVFS(editorState?.project);
  // V2 Files: Verwende projectContent.tabs als Source of Truth (enthält dynamisch hinzugefügte Dateien)
  const v2Files: FileDefinition[] = isV2Project 
    ? (editorState?.data?.projectContent?.tabs || []).map((tab: any) => ({
        path: tab.path,
        name: tab.name,
        language: tab.language,
        active: tab.active,
        replaceKey: tab.replaceKey,
        readOnly: tab.readOnly
      }))
    : [];
  const filesEnabled = isV2Project && v2Files.length > 0;
  
  // Left panel shows tasks OR files (Version 2.0)
  const showLeftPanel = !!taskAreaEnabled || filesEnabled;
  const taskArea = taskAreaEnabled && <TaskArea />;  
  const taskAreaSwitch = taskAreaEnabled && (
    <ModernHeaderButton 
      icon="list-check" 
      onClick={switchTaskArea} 
      title="Aufgaben anzeigen" 
      text="Aufgaben" 
      variant="secondary" 
    />
  )
  const taskAreaCard = taskAreaEnabled && <TaskAreaCard closeArea={switchTaskArea} taskOpened={editorState.cfg?.editorFeatures?.taskAccordionOpenedOnDefault} />

  const hasCodeEntryHelper = editorState.cfg?.editorFeatures?.codeEntryHelper;
  const codeEntryLink = editorState.cfg?.editorFeatures?.codeEntryLink;
  const codeEntryHelper = <CodeEntryHelper chars={hasCodeEntryHelper} link={codeEntryLink} show={hasCodeEntryHelper} />;
  const codeEntryHelperSwitch = <CodeEntryAreaSwitch isVisible={switchCodeEntryArea} />
  const codeEntryArea = <CodeEntryHelperArea chars={hasCodeEntryHelper} link={codeEntryLink} open={showCodeEntryHelperArea} close={switchCodeEntryArea} />

  const collaborationEnabled = getDomainCfg().viewSettings.features.collaborationEnabled;
  const partyManager = collaborationEnabled && <PartyManager />;

  const supportEnabled = getDomainCfg().viewSettings.features.chatAvailable;
  const agentEnabled = supportEnabled && getDomainCfg().viewSettings.features?.browserAgentEnabled === true && isV2Project;
  const supportAreaSwitch = supportEnabled && (
    <ModernHeaderButton 
      icon="wand-magic-sparkles" 
      onClick={showSupportChatArea} 
      title="KiRa AI-Chat" 
      text="KiRa AI-Chat" 
      variant="secondary" 
    />
  )
  const supportChatArea = supportEnabled && (
    agentEnabled ? (
      <div className="tabChatParent">
        <AgentChat key={editorState?.project?.id ?? "no-project"} projectId={editorState?.project?.id} />
      </div>
    ) : getDomainCfg().chatSettings?.useWebsocket ? (
      <div className="tabChatParent">
        <AuthenticatedSupportChat hasProjectContext={true} />
      </div>
    ) : (
      <div className="tabChatParent">
        <SupportChat hasProjectContext={true} />
        {chatAreaIntegration === "tabbed" && (
          <Button 
            className="floating-chat-button" 
            onClick={switchToFloatingChat}
            style={{ position: 'absolute', right: '10px', top: '10px' }}
          >
            <FontAwesomeIcon icon="external-link-alt" /> Open Floating
          </Button>
        )}
      </div>
    )
  );
  const showFloatingSupport = chatAreaIntegration === "floating";
  const showTabbedSupport = chatAreaIntegration === "tabbed";
  const floatingRequestSupportButton = (
    <RequestSupportButton 
      title={editorState.cfg?.editorFeatures?.chatFunctionName || "Chat" }
      supportRequested={() => { 
        console.log('support');
        setIsChatVisible(true);
      }} 
      show={true} 
      floating={true} 
      showChat={isChatVisible && chatAreaIntegration === "floating"}
      closeRequested={() => { 
        console.log('close');
        if (lpdeLayout) {
          setChatAreaIntegration("tabbed");
        } else {
          setIsChatVisible(false);
        }
      }}
    />
  );


  const responsiveBottomArea = <>
    { !showTaskArea && !showCodeEntryHelperArea && !showResponsiveSupport && showIframeView && appFrame }
    { showCodeEntryHelperArea && codeEntryArea }
    { showTaskArea && <div className="responsive-task-area">{taskAreaCard}</div>}
    { showResponsiveSupport && supportChatArea }
  </>

  const bottomMenu = <>{ responsiveView && 
    <div className="bottomMenu ">
      <CodeControlsMenu 
        runCode={showAppArea} 
        cfg={editorState?.cfg}
        fullscreenButton={
          !showTaskArea && !showCodeEntryHelperArea && !showResponsiveSupport && showIframeView && timeStamp && editorState?.cfg?.editorFeatures?.fullscreenButtonEnabled !== false ? (
            <ModernHeaderButton
              icon="expand"
              onClick={() => {
                if (responsiveOutputRef.current) {
                  requestFullscreen(responsiveOutputRef.current);
                }
              }}
              title="Vollbild anzeigen"
              text="Vollbild"
              variant="secondary"
            />
          ) : null
        }
      />
      <span>{hasCodeEntryHelper && codeEntryHelperSwitch}</span>
      { supportAreaSwitch }
      { taskAreaSwitch }
    </div>
  }</>

  const onDividerPointerDown = (e: React.PointerEvent) => {
    if (dividerRef.current) {
      dividerRef.current.setPointerCapture(e.pointerId);
      pointerIdRef.current = e.pointerId;
    }
    setDragging(true);
    setDragStartX(e.clientX);
    setStartCodeWidth(codePanelWidth);
  };

  const onHorizontalDividerPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (horizontalDividerRef.current) {
      try {
        horizontalDividerRef.current.setPointerCapture(e.pointerId);
        horizontalPointerIdRef.current = e.pointerId;
      } catch (err) {
        console.warn('Failed to capture pointer:', err);
      }
    }
    setHorizontalDragging(true);
    setDragStartY(e.clientY);
    setStartEditorHeight(editorHeight);
  };

  return (
    <div onKeyUp={handleKeyUp}>
      <LoginRegisterModal />
      <QuestionModal />
      <ShareModal />
      <Onboarding view={EDITOR_VIEW_NAME}/>
      { responsiveView &&
        <>
        <div className="responsive-editor-header">
          <div className="responsive-header-left">
            {getDomainCfg().product?.logoUrl ? (
              <img src={getDomainCfg().product.logoUrl} className="responsive-logo-img" alt={getDomainCfg().product?.name || 'Logo'} />
            ) : (
              <div className="responsive-logo-text">{getDomainCfg().product?.name || 'CodeLearning'}</div>
            )}
          </div>
          <div className="responsive-header-center">
            <div className="responsive-project-title">
              <ProjectNameLabel project={editorState?.project} />
            </div>
          </div>
          <div className="responsive-header-right">
            <MenuArea>
              <MainMenu withProject={true} menuitems={editorState?.cfg?.menus.mainmenu} burger={true} alwaysShow={true} />
            </MenuArea>
          </div>
        </div>
        <div className="responsive-editor-container">
          <div className="responsive-editor-section" style={{height: `${editorHeight}px`}}>
            {showIframeView && menu}
            <ProjectEditor width={"100%"} height={"100%"} currentTheme={currentTheme} />
          </div>
          <div className="responsive-horizontal-divider" ref={horizontalDividerRef} onPointerDown={onHorizontalDividerPointerDown}></div>
          <div className="responsive-output-section" ref={responsiveOutputRef}>
            {isInFullscreen && (
              <button 
                className="output-fullscreen-close-button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  exitFullscreen();
                }}
                title="Vollbild verlassen"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            )}
            {responsiveBottomArea}
          </div>
          {bottomMenu}
          {responsiveView && <EditProjectDataModal />}
          {showAssetsLib && responsiveView && (
            <FullscreenOverlay 
              title={translate('open-lib-menu')}
              onClose={() => {
                const currentEditorState = getViewData(EDITOR_VIEW_NAME);
                currentEditorState.data = {
                  ...currentEditorState.data,
                  assetsVisible: false
                };
                setViewData(EDITOR_VIEW_NAME, currentEditorState);
              }}
            >
              {assetsLib}
            </FullscreenOverlay>
          )}
        </div>
        </>
      }
      { !responsiveView &&
        <div className="modern-editor" ref={containerRef}>
          <div className="editor-header">
            <div className="header-left">
              {getDomainCfg().product?.logoUrl ? (
                <img src={getDomainCfg().product.logoUrl} className="logo-img" alt={getDomainCfg().product?.name || 'Logo'} style={{maxHeight: '40px', maxWidth: '200px'}} />
              ) : (
                <div className="logo-text">{getDomainCfg().product?.name || 'CodeLearning'}</div>
              )}
              <div className="project-info">
                <h1 className="project-title"><ProjectNameLabel project={editorState?.project} /></h1>
                <div className="project-meta">
                  <LanguageBadge language={editorState?.project?.language} />
                  <ProgressIndicator editorState={editorState} />
                </div>
              </div>
            </div>
            <div className="editor-actions" style={{gap: '12px', marginLeft: 'auto'}}>
              {/* Rectangular Run/Reset and Exit, no Share/Help buttons */}
              <span>
                <CodeControlsMenu cfg={{ resetButtonEnabled: true, theme: 'modern' }} />
              </span>
              <span>
                <EditorNavMenu />
              </span>
              <span>
                <MenuArea>
                  <MainMenu withProject={true} menuitems={editorState?.cfg?.menus.mainmenu} burger={true} alwaysShow={true} />
                </MenuArea>
              </span>
            </div>
          </div>
          <div className="editor-main">
            { showLeftPanel && (
              <div className={`tasks-panel ${tasksCollapsed ? 'collapsed' : ''}`}>
                <div className="panel-header">
                  <div className="panel-tabs">
                    {taskAreaEnabled && (
                      <button 
                        className={`panel-tab ${leftPanelTab === 'tasks' ? 'active' : ''}`}
                        onClick={() => setLeftPanelTab('tasks')}
                      >
                        <FontAwesomeIcon icon={faListCheck} />&nbsp;Aufgaben
                      </button>
                    )}
                    {filesEnabled && (
                      <button 
                        className={`panel-tab ${leftPanelTab === 'files' ? 'active' : ''}`}
                        onClick={() => setLeftPanelTab('files')}
                      >
                        <FontAwesomeIcon icon={faFolderOpen} />&nbsp;Dateien
                      </button>
                    )}
                  </div>
                  <button className="panel-toggle" onClick={() => setTasksCollapsed(!tasksCollapsed)}>{tasksCollapsed ? '›' : '‹'}</button>
                </div>
                <div className="panel-content">
                  {leftPanelTab === 'tasks' && taskAreaEnabled && taskArea}
                  {leftPanelTab === 'files' && filesEnabled && (
                    <FilePanel
                      projectId={editorState?.project?.id || ''}
                      files={v2Files}
                      activeFile={editorState?.data?.activeFilePath}
                      onFileSelect={(file: FileDefinition) => {
                        // Datei als Tab öffnen
                        console.log('Opening file:', file.name);
                        const currentOpenPaths = editorState?.data?.openFilePaths || 
                          v2Files.filter((f: FileDefinition) => f.active).map((f: FileDefinition) => f.path);
                        
                        // Nur hinzufügen wenn noch nicht offen
                        const newOpenPaths = currentOpenPaths.includes(file.path) 
                          ? currentOpenPaths 
                          : [...currentOpenPaths, file.path];
                        
                        setEditorState({
                          ...editorState,
                          data: { 
                            ...editorState?.data, 
                            activeFilePath: file.path,
                            openFilePaths: newOpenPaths
                          }
                        });
                      }}
                      onFileCreate={(filename: string) => {
                        const result = createNewFile(filename, v2Files);
                        if (result.success && result.file) {
                          // Füge neue Datei zu projectContent.tabs hinzu
                          const currentTabs = editorState?.data?.projectContent?.tabs || [];
                          const newTab = {
                            ...result.file,
                            code: '',
                            index: currentTabs.length
                          };
                          const updatedTabs = [...currentTabs, newTab];
                          
                          setEditorState({
                            ...editorState,
                            data: {
                              ...editorState?.data,
                              projectContent: {
                                ...editorState?.data?.projectContent,
                                tabs: updatedTabs
                              },
                              // Öffne die neue Datei direkt
                              openFilePaths: [...(editorState?.data?.openFilePaths || []), result.file.path],
                              activeFilePath: result.file.path
                            }
                          });
                          console.log('Created file:', filename);
                        } else {
                          alert(result.error || 'Fehler beim Erstellen der Datei');
                        }
                      }}
                      onFileDelete={(file: FileDefinition) => {
                        if (window.confirm(`Datei "${file.name}" wirklich löschen?`)) {
                          // Entferne Datei aus projectContent.tabs
                          const currentTabs = editorState?.data?.projectContent?.tabs || [];
                          const updatedTabs = currentTabs.filter((t: any) => t.path !== file.path);
                          
                          // Entferne aus openFilePaths falls offen
                          const currentOpenPaths = editorState?.data?.openFilePaths || [];
                          const updatedOpenPaths = currentOpenPaths.filter((p: string) => p !== file.path);
                          
                          // Entferne aus v2EditorChanges falls vorhanden
                          const currentChanges = new Map(editorState?.data?.v2EditorChanges || []);
                          currentChanges.delete(file.path);
                          
                          // Neuen activeFilePath setzen falls gelöschte Datei aktiv war
                          let newActiveFilePath = editorState?.data?.activeFilePath;
                          if (newActiveFilePath === file.path) {
                            newActiveFilePath = updatedOpenPaths.length > 0 ? updatedOpenPaths[0] : null;
                          }
                          
                          setEditorState({
                            ...editorState,
                            data: {
                              ...editorState?.data,
                              projectContent: {
                                ...editorState?.data?.projectContent,
                                tabs: updatedTabs
                              },
                              openFilePaths: updatedOpenPaths,
                              activeFilePath: newActiveFilePath,
                              v2EditorChanges: currentChanges
                            }
                          });
                          console.log('Deleted file:', file.name);
                        }
                      }}
                      onFileUpload={async (file: File) => {
                        const projectId = editorState?.project?.id || '';
                        const result = await processUploadedFile(file, projectId, v2Files);
                        
                        if (result.success && result.file && result.content !== undefined) {
                          // Füge Datei zu projectContent.tabs hinzu
                          const currentTabs = editorState?.data?.projectContent?.tabs || [];
                          const newTab = {
                            ...result.file,
                            code: result.content,
                            index: currentTabs.length
                          };
                          const updatedTabs = [...currentTabs, newTab];
                          
                          setEditorState({
                            ...editorState,
                            data: {
                              ...editorState?.data,
                              projectContent: {
                                ...editorState?.data?.projectContent,
                                tabs: updatedTabs
                              }
                            }
                          });
                          console.log('Uploaded file:', file.name);
                        } else {
                          alert(result.error || 'Fehler beim Hochladen');
                        }
                      }}
                      onProjectDownload={async () => {
                        try {
                          const projectId = editorState?.project?.id || 'project';
                          const tabs = editorState?.data?.projectContent?.tabs || [];
                          const v2Changes = (editorState?.data?.v2EditorChanges || {}) as Record<string, string>;
                          
                          // Erstelle Map mit aktuellem Inhalt (Original + Änderungen)
                          const fileContents = new Map<string, string>();
                          for (const tab of tabs) {
                            const content = (tab.path in v2Changes && v2Changes[tab.path] !== undefined) ? v2Changes[tab.path] : tab.code;
                            fileContents.set(tab.path, content);
                          }
                          
                          await downloadProjectAsZip(projectId, tabs, fileContents);
                        } catch (error: any) {
                          alert('Download fehlgeschlagen: ' + (error.message || 'Unbekannter Fehler'));
                        }
                      }}
                    />
                  )}
                </div>
              </div>
            )}
            <div className="code-section" style={{flex: `0 0 ${codePanelWidth}px`}}>
              {!(editorState?.data?.projectContent?.tabs && editorState?.data?.projectContent?.tabs.length > 0) && (
                <div className="code-header">
                  <div className="panel-tabs">
                    <button className="panel-tab active">Code</button>
                  </div>
                  <div className="editor-actions" style={{marginLeft:'auto', display:'flex', gap: '8px'}}>
                    <button className="panel-tab" onClick={() => { try { (window as any).__monaco_editor__?.trigger('keyboard', 'undo', null); } catch {} }}>
                      <FontAwesomeIcon icon={faRotateLeft} />
                    </button>
                    <button className="panel-tab" onClick={() => { try { (window as any).__monaco_editor__?.trigger('keyboard', 'redo', null); } catch {} }}>
                      <FontAwesomeIcon icon={faRotateRight} />
                    </button>
                  </div>
                </div>
              )}
              <ProjectEditor
                width={codePanelWidth.toString()}
                height={"100%"}
                currentTheme={currentTheme}
                rightSidebarCollapsed={rightCollapsed}
                onRightSidebarToggle={toggleRightSidebar}
              />
            </div>
            { !rightCollapsed && (
              <div className="resizable-divider" ref={dividerRef} onPointerDown={onDividerPointerDown}></div>
            )}
            <div className={`output-panel ${rightCollapsed ? 'collapsed' : ''}`}>
              <div className="panel-header">
                {!rightCollapsed && (
                <>
                <div className="panel-tabs">
                  <button className={`panel-tab ${editorState?.data?.rightTab === 'ai' ? '' : 'active'}`} onClick={() => {
                    // Stelle sicher, dass alle Properties erhalten bleiben
                    const existingData = editorState?.data || {};
                    const newState = { ...editorState, data: { ...existingData, rightTab: 'app' } };
                    setEditorState(newState);
                  }}>
                    <FontAwesomeIcon icon={faTv} />&nbsp;Ausgabe
                  </button>
                  {supportEnabled && !showResponsiveSupport && <button className={`panel-tab ${editorState?.data?.rightTab === 'ai' ? 'active' : ''}`} onClick={() => {
                    // Stelle sicher, dass alle Properties erhalten bleiben
                    const existingData = editorState?.data || {};
                    const newState = { ...editorState, data: { ...existingData, rightTab: 'ai' } };
                    setEditorState(newState);
                  }}>
                    <FontAwesomeIcon icon={faRobot} />&nbsp;{agentEnabled ? 'KiRa - AI Agent' : 'KiRa - AI Chat'}
                  </button>}
                </div>
                { (editorState?.data?.rightTab !== 'ai') && (
                <div className="output-status">
                  <div className="status-left">
                    {backendActive(editorState) && backendState?.sessionState ? (
                      <>
                        {getStatusIndicator(backendState.sessionState)}
                        <span className="text">
                          {translate(getBackendStatusText(backendState.sessionState))}
                        </span>
                      </>
                    ) : (
                      <>
                        {editorState?.data?.executionRequested && (<span className="dot">▶</span>)}
                        {!editorState?.data?.executionRequested && editorState?.data?.errors && editorState?.data?.errors.length > 0 && (<span className="dot error">✗</span>)}
                        <span className="text">
                          {editorState?.data?.executionRequested 
                            ? 'Wird ausgeführt…' 
                            : (editorState?.data?.errors && editorState?.data?.errors.length > 0 
                              ? 'Fehler aufgetreten' 
                              : 'Bereit zum Starten')}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                )}
                { (editorState?.data?.rightTab !== 'ai') && showIframeView && timeStamp && editorState?.cfg?.editorFeatures?.fullscreenButtonEnabled !== false && (
                  <button 
                    className="output-fullscreen-button" 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (outputContentRef.current) {
                        requestFullscreen(outputContentRef.current);
                      }
                    }}
                    title="Vollbild anzeigen"
                  >
                    <FontAwesomeIcon icon={faExpand} />
                  </button>
                )}
                </>
                )}
                <button className="panel-toggle" title={rightCollapsed ? 'Öffnen' : 'Einklappen'} onClick={() => {
                  toggleRightSidebar();
                }}>
                  {rightCollapsed ? '‹' : '›'}
                </button>
              </div>
              <div className="output-panel-content" ref={outputContentRef}>
                {isInFullscreen && (
                  <button 
                    className="output-fullscreen-close-button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      exitFullscreen();
                    }}
                    title="Vollbild verlassen"
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                )}
                { (editorState?.data?.rightTab !== 'ai') && (showIframeView && (
                  <AppIFrame doc={errorCode || executeCode} timeStamp={timeStamp} classNames="ratio ratio-16x9" fullScreen={false} landscape={false} muteSound={false} hasAppBorder={false} />
                ))}
                { (editorState?.data?.rightTab !== 'ai') && showAssetsLib && assetsLib }
                { (editorState?.data?.rightTab === 'ai') && supportChatArea }
              </div>
            </div>
          </div>
        </div>
      }
      <InfoToast />
      <PopperHelper />
      <StepPopper steps={editorState?.project?.steps} />
      { supportEnabled && (chatAreaIntegration === "floating" || showFloatingSupport) && !responsiveView && floatingRequestSupportButton }
      {!responsiveView && <EditProjectDataModal />}
    </div>
  );
}